import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiClock,
  FiCopy,
  FiEdit2,
  FiFileText,
  FiInfo,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUserCheck,
} from "react-icons/fi";
import type { PayoutAccount } from "./payoutData";
import PayoutBeneficiaryFlowSteps from "./PayoutBeneficiaryFlowSteps";
import { ensurePayoutsAccessToken, isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import {
  accountFromApiRecord,
  apiString,
  beneficiaryFromApiRecord,
  formatApiDateTime,
} from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  isPayoutStepUpEnabled,
  pickString,
  readSessionJson,
  stableIdempotencyKey,
  verifyStepUp,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import {
  normalizeBankAccountNumber,
  normalizeEmail,
  normalizeIfsc,
  normalizeIndianMobile,
  validateRequiredBankAccountNumber,
  validateRequiredConfirmAccountNumber,
  validateRequiredEmail,
  validateRequiredIfsc,
  validateRequiredIndianMobile,
} from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";
import { contextualValidationMessage } from "./payoutAlertMessages";

type SelectOption = {
  label: string;
  value: string;
  meta?: string;
};

type BeneficiaryEditDraft = {
  beneficiaryId: string;
  payoutAccount?: SelectOption | null;
  name: string;
  beneficiaryName?: string;
  category: SelectOption | string | null;
  mobile: string;
  mobileNumber?: string;
  email: string;
  accountNumber: string;
  confirmAccountNumber: string;
  ifsc: string;
  ifscCode?: string;
  accountType: SelectOption | string | null;
  branchName: string;
  remarks?: string;
  tags?: string;
  beneficiaryNameVerified?: boolean;
  bankName?: string;
  existingLast4?: string;
  fundingAccountId?: string;
};

type BulkBeneficiaryDraft = {
  fileName?: string;
  sizeKb?: number;
  rows?: Record<string, unknown>[];
};

type BulkBeneficiaryResult = {
  results?: Record<string, unknown>[];
  data?: {
    results?: Record<string, unknown>[];
  };
};

const emptyPayoutAccount: PayoutAccount = {
  id: "",
  name: "",
  school: "",
  bank: "",
  logoUrl: "",
  accountNumber: "",
  ifsc: "",
  currency: "INR",
  addedOn: "",
  availableBalance: 0,
  accountBalance: 0,
  fundsOnHold: 0,
  overdraftBalance: 0,
  overdraftLimit: 0,
  lowBalanceThreshold: 0,
  accent: "bg-[#6687FF]",
};

const LOCAL_UAT_BENE = {
  account: "0153009500289697",
  ifscPrefix: "PUNB",
  name: "SANTOSH KUMAR",
};

const BHARAT_UAT_BENE = {
  account: "123456789012",
  ifsc: "HDFC0001782",
  name: "",
};

const beneficiaryCategoryOptions: SelectOption[] = [
  { label: "Vendor", value: "vendor" },
  { label: "Employee", value: "employee" },
  { label: "Student", value: "student" },
  { label: "Scholarship", value: "scholarship" },
  { label: "Refund", value: "refund" },
  { label: "Reimbursement", value: "reimbursement" },
  { label: "Transport Vendor", value: "transport-vendor" },
  { label: "Utility Provider", value: "utility-provider" },
  { label: "Others", value: "others" },
];

const accountTypeOptions: SelectOption[] = [
  { label: "Savings", value: "savings" },
  { label: "Current", value: "current" },
  { label: "Salary", value: "salary" },
  { label: "NRE", value: "nre", meta: "Non-resident external" },
  { label: "NRO", value: "nro", meta: "Non-resident ordinary" },
  { label: "Overdraft", value: "overdraft" },
  { label: "Cash Credit", value: "cash-credit" },
];

const displayText = (value?: string) => value?.trim() || "-";

const optionText = (value: SelectOption | string | null | undefined, fallback = "-") => {
  if (typeof value === "string") return displayText(value);
  return displayText(pickString(value, ["label", "value"], fallback));
};

const namesMatch = (a: string, b: string) => {
  const left = a.trim().toUpperCase().replace(/\s+/g, " ");
  const right = b.trim().toUpperCase().replace(/\s+/g, " ");
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
};

const maskedAccount = (accountNumber?: string, fallbackLast4?: string) => {
  const last4 =
    accountNumber?.replace(/\D/g, "").slice(-4) ||
    fallbackLast4?.replace(/\D/g, "").slice(-4) ||
    "";
  return last4 ? `**** ${last4}` : "-";
};

const fileExtension = (fileName?: string) => {
  const ext = displayText(fileName).split(".").pop()?.toUpperCase();
  return ext && ext !== "-" ? ext.slice(0, 4) : "FILE";
};

const findBulkBeneficiaryAttachment = (beneficiaryId: string) => {
  const draft = readSessionJson<BulkBeneficiaryDraft>(
    "payoutBulkBeneficiaryDraft",
    {},
  );
  const result = readSessionJson<BulkBeneficiaryResult>(
    "payoutBulkBeneficiaryResult",
    {},
  );
  const resultRows = Array.isArray(result.results)
    ? result.results
    : Array.isArray(result.data?.results)
      ? result.data.results
      : [];
  const matched = resultRows.find(
    (row) =>
      pickString(row, ["beneficiary_id", "beneficiaryId", "id"]) ===
      beneficiaryId,
  );
  if (!matched || !draft.fileName) return null;
  const index = Number(matched.index);
  const rowCount = Array.isArray(draft.rows) ? draft.rows.length : 0;

  return {
    fileName: draft.fileName,
    size: draft.sizeKb && draft.sizeKb > 0 ? `${draft.sizeKb} KB` : "-",
    ext: fileExtension(draft.fileName),
    rowLabel: Number.isFinite(index) ? `Row ${index + 1} of ${rowCount || "-"}` : `${rowCount || "-"} rows`,
  };
};

const initialsFromName = (name: string) => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return initials || "-";
};

const optionFromLabel = (options: SelectOption[], label: string, fallback?: SelectOption) => {
  if (!label) return fallback || null;
  const normalized = label.toLowerCase();
  return (
    options.find(
      (option) =>
        option.label.toLowerCase() === normalized ||
        option.value.toLowerCase() === normalized,
    ) ||
    fallback ||
    { label, value: label.toLowerCase().replace(/\s+/g, "-") }
  );
};

const decodeBeneficiary = (value?: string) => {
  if (!value) return "-";
  const decoded = decodeURIComponent(value);
  // Keep real beneficiary ids intact (ben_uuid).
  if (/^ben_/i.test(decoded)) return decoded;
  return decoded.replace(/-/g, " ");
};

const beneficiaryRouteKey = (value?: string) =>
  value ? decodeURIComponent(value) : "";

const statusBadgeClass: Record<string, string> = {
  Verified: "bg-[#DCFCE7] text-[#16A34A]",
  Pending: "bg-[#FEF3C7] text-[#B45309]",
  Failed: "bg-[#FEE2E2] text-[#B91C1C]",
  Inactive: "bg-[#F3F4F6] text-[#6B7280]",
};

function DetailLine({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] py-4 text-sm md:grid-cols-[220px_minmax(0,1fr)]">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <div className="font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

const displayDetailValue = (value?: React.ReactNode) => {
  if (value === undefined || value === null || value === "") return "-";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "-";
  return value;
};

const arrayFromApiValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const objectFromApiKeys = (
  row: Record<string, unknown>,
  keys: string[],
) => {
  for (const key of keys) {
    const value = row[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
  }
  return {};
};

const beneficiaryDetailFromApiRecord = (
  row: Record<string, unknown>,
  fallback: Record<string, unknown>,
) => {
  const mapped = beneficiaryFromApiRecord(row, fallback);
  const fundingAccount = objectFromApiKeys(row, [
    "funding_account",
    "fundingAccount",
    "payout_account",
    "payoutAccount",
    "account",
  ]);
  const bankAccountNumber = apiString(
    row,
    [
      "account_number",
      "accountNumber",
      "bank_account_number",
      "bankAccountNumber",
      "beneficiary_account_number",
      "beneficiaryAccountNumber",
      "masked_account_number",
      "maskedAccountNumber",
    ],
    mapped.accountNumber,
  );
  const tags = arrayFromApiValue(row.tags || row.tag_names || row.tagNames);
  const createdAt = apiString(row, ["created_at", "createdAt", "created_on", "createdOn"], "");
  const updatedAt = apiString(row, ["updated_at", "updatedAt", "modified_at", "modifiedAt"], "");
  const verifiedAt = apiString(row, ["verified_at", "verifiedAt", "verification_at", "verificationAt"], "");

  return {
    ...mapped,
    payoutAccount: apiString(
      row,
      [
        "funding_account_name",
        "fundingAccountName",
        "funding_account_label",
        "fundingAccountLabel",
        "payout_account_name",
        "payoutAccountName",
        "account_name",
        "accountName",
      ],
      apiString(fundingAccount, ["display_name", "displayName", "name", "account_name", "accountName"], ""),
    ),
    payoutAccountId: apiString(
      row,
      ["funding_account_id", "fundingAccountId", "payout_account_id", "payoutAccountId", "account_id", "accountId"],
      apiString(fundingAccount, ["id", "account_id", "accountId"], ""),
    ),
    payoutAccountMeta: apiString(
      row,
      ["funding_account_meta", "fundingAccountMeta", "payout_account_meta", "payoutAccountMeta"],
      [
        apiString(fundingAccount, ["bank_name", "bankName", "bank"], ""),
        apiString(fundingAccount, ["account_number_masked", "accountNumberMasked", "account_number", "accountNumber"], ""),
      ].filter(Boolean).join(" "),
    ),
    mobile: apiString(row, ["mobile", "mobile_number", "mobileNumber", "phone", "phone_number", "phoneNumber"], String(fallback.mobile || "")),
    email: apiString(row, ["email", "email_id", "emailId"], String(fallback.email || "")),
    accountNumber: bankAccountNumber,
    ifsc: apiString(row, ["ifsc", "ifsc_code", "ifscCode"], mapped.ifsc),
    bank: apiString(row, ["bank_name", "bankName", "bank"], mapped.bank),
    accountType: apiString(row, ["account_type", "accountType"], mapped.accountType),
    branch: apiString(row, ["branch_name", "branchName", "branch"], String(fallback.branch || "")),
    remarks: apiString(row, ["remarks", "remark", "purpose", "description", "notes"], ""),
    tags,
    nameAsPerBank: apiString(row, ["name_as_per_bank", "nameAsPerBank", "legal_name", "legalName"], mapped.subtitle),
    createdAt: createdAt ? formatApiDateTime(createdAt, createdAt) : "",
    updatedAt: updatedAt ? formatApiDateTime(updatedAt, updatedAt) : "",
    verifiedAt: verifiedAt ? formatApiDateTime(verifiedAt, verifiedAt) : "",
    createdBy: apiString(row, ["created_by", "createdBy", "maker_user_id", "makerUserId"], ""),
    verificationStatus: apiString(row, ["verification_status", "verificationStatus", "status"], mapped.status),
  };
};

function FieldLabel({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
      {children}
      {required && <span className="text-[#EF4444]"> *</span>}
    </span>
  );
}

function TextInput({
  label,
  placeholder,
  required = false,
  value,
  onChange,
  error,
  inputMode,
  maxLength,
  type = "text",
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
}) {
  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <input
        value={value}
        type={type}
        onChange={(event) => onChange(event.target.value)}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        className={
          "h-11 w-full rounded-[6px] border bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0] " +
          (error ? "border-[#EF4444]" : "border-[#E0E5F2] focus:border-[#1B163B]")
        }
      />
      {error && (
        <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>
      )}
    </label>
  );
}

function SearchableDropdown({
  label,
  placeholder,
  options,
  searchPlaceholder = "Search options",
  required = false,
  value,
  onChange,
  error,
}: {
  label: string;
  placeholder: string;
  options: SelectOption[];
  searchPlaceholder?: string;
  required?: boolean;
  value: SelectOption | null;
  onChange: (value: SelectOption) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredOptions = options.filter((option) =>
    (option.label + " " + (option.meta || ""))
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  return (
    <div className="relative">
      <FieldLabel required={required}>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={
          "flex h-11 w-full items-center justify-between rounded-[6px] border bg-white px-3 text-left text-sm font-semibold outline-none " +
          (error ? "border-[#EF4444]" : open ? "border-[#6687FF]" : "border-[#E0E5F2]")
        }
      >
        <span className={value ? "truncate text-[#1B163B]" : "truncate text-[#A3AED0]"}>
          {value?.label || placeholder}
          {value?.meta && (
            <span className="ml-1 font-medium text-[#505E8E]">{value.meta}</span>
          )}
        </span>
        <FiChevronDown
          className={
            "shrink-0 text-[#505E8E] transition-transform " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
          <div className="flex h-10 items-center gap-2 border-b border-[#E0E5F2] px-3">
            <FiSearch className="shrink-0 text-[#505E8E]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
              placeholder={searchPlaceholder}
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto py-1">
            {filteredOptions.map((option) => {
              const active = value?.value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={
                    "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-medium hover:bg-[#A9B2CF1A] " +
                    (active ? "text-[#1B163B]" : "text-[#505E8E]")
                  }
                >
                  <span className="truncate">
                    {option.label}
                    {option.meta && (
                      <span className="ml-1 text-[#505E8E]">{option.meta}</span>
                    )}
                  </span>
                  {active && <FiCheck className="shrink-0 text-[#6687FF]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && (
        <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>
      )}
    </div>
  );
}

function SummaryLine({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-4 text-sm last:mb-0">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <span className="max-w-[220px] text-right font-semibold text-[#1B163B]">
        {value || "-"}
      </span>
    </div>
  );
}

function OtpPanel({
  title,
  description,
  cta,
  to,
  danger = false,
  onSubmit,
}: {
  title: string;
  description: string;
  cta: string;
  to: string;
  danger?: boolean;
  onSubmit?: (payload: {
    method: "sms" | "authenticator";
    code: string;
  }) => Promise<void>;
}) {
  const navigate = useNavigate();
  const method = "authenticator" as const;
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [, setSubmitError] = useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const code = useMemo(() => otp.join(""), [otp]);
  const ready = code.length === 6;
  const instruction = "Enter the 6-digit code from your Google Authenticator app.";

  const updateDigit = (index: number, value: string) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      const nextOtp = [...otp];
      nextOtp[index] = "";
      setOtp(nextOtp);
      return;
    }
    if (digits.length > 1) {
      handlePaste(digits);
      return;
    }
    const nextOtp = [...otp];
    nextOtp[index] = digits;
    setOtp(nextOtp);
    if (index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    inputsRef.current[Math.min(digits.length, 5)]?.focus();
  };

  const handleSubmit = async () => {
    if (!ready || submitting) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit({ method, code });
      } else {
        navigate(to);
      }
    } catch (error) {
      showSubmitError(errorMessage(error, `${title}: unable to complete this action. Please try again.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-xl text-[#1B163B]">
            {danger ? <FiTrash2 /> : <FiUserCheck />}
          </div>
          <div>
            <h2 className="text-[18px] font-semibold text-[#1B163B]">{title}</h2>
            <p className="mt-1 text-sm font-medium text-[#505E8E]">
              {description} {instruction}
            </p>
          </div>
        </div>

        <div>
          <div className="rounded-[8px] border border-[#1B163B] bg-[#1B163B0D] px-5 py-4 text-left">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
                  <FiShield />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-[#1B163B]">
                    Google Authenticator
                  </span>
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    Use your linked authenticator app
                  </span>
                </span>
              </span>
              <FiCheck className="text-lg text-[#16A34A]" />
            </div>
          </div>
        </div>

        <div className="mt-8">
          <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
            Verification Code
          </label>
          <div className="flex flex-wrap gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(element) => {
                  inputsRef.current[index] = element;
                }}
                value={digit}
                onChange={(event) => updateDigit(index, event.target.value)}
                onPaste={(event) => {
                  event.preventDefault();
                  handlePaste(event.clipboardData.getData("text"));
                }}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && !otp[index] && index > 0) {
                    inputsRef.current[index - 1]?.focus();
                  }
                }}
                inputMode="numeric"
                className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
              />
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-5 text-sm font-semibold">
            <button
              type="button"
              onClick={() => setOtp(["", "", "", "", "", ""])}
              className="flex items-center gap-2 text-[#1B163B]"
            >
              <FiRefreshCw />
              Resend code
            </button>
          </div>
        </div>
      </section>

      <div className="mt-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-4 new-shadow">
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to="/payouts/beneficiaries"
            className="flex h-11 min-w-[140px] items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            <FiArrowLeft />
            Back
          </Link>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!ready || submitting}
            style={{
              backgroundColor: ready && !submitting
                ? danger
                  ? "#D51F35"
                  : "#1B163B"
                : "#A3AED0",
              color: "#FFFFFF",
            }}
            className={
              "ml-auto flex h-11 min-w-[200px] items-center justify-center gap-3 rounded-[6px] px-8 text-sm font-semibold text-white shadow-sm " +
              (ready && !submitting ? "hover:brightness-95" : "cursor-not-allowed")
            }
          >
            {submitting ? "Continuing..." : "Continue"}
            <FiArrowRight className="text-lg" />
          </button>
        </div>
        {!ready && (
          <p className="mt-2 text-right text-xs font-semibold text-[#EF4444]">
            Enter all 6 digits to enable Continue
          </p>
        )}
      </div>
    </div>
  );
}

function BeneficiarySummary({
  name,
  details,
}: {
  name: string;
  details?: {
    category?: string;
    payoutAccount?: string;
    payoutAccountMeta?: string;
    bank?: string;
    accountNumber?: string;
    ifsc?: string;
    accountType?: string;
    branch?: string;
    mobile?: string;
    email?: string;
    status?: string;
    tone?: "new" | "edit" | "delete";
  };
}) {
  const safeName = displayText(name);
  const toneLabel =
    details?.tone === "delete"
      ? "Delete Beneficiary"
      : details?.tone === "edit"
        ? "Edit Beneficiary"
        : "New Beneficiary";

  return (
    <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
      <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
        Beneficiary Summary
      </h2>
      <div className="mb-4 h-px bg-[#E0E5F2]" />
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F4F7FE] text-lg font-semibold text-[#505E8E]">
          {initialsFromName(safeName)}
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1B163B]">{safeName}</p>
          <span className="mt-2 inline-flex rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
            {toneLabel}
          </span>
        </div>
      </div>
      <div className="mb-5 h-px bg-[#E0E5F2]" />
      <h3 className="mb-3 text-sm font-semibold text-[#1B163B]">Bank Details</h3>
      <SummaryLine label="Payout Account" value={details?.payoutAccount} />
      <SummaryLine label="Payout Meta" value={details?.payoutAccountMeta} />
      <SummaryLine label="Bank" value={details?.bank} />
      <SummaryLine label="Account Number" value={details?.accountNumber} />
      <SummaryLine label="IFSC Code" value={details?.ifsc} />
      <SummaryLine label="Account Type" value={details?.accountType} />
      <SummaryLine label="Branch" value={details?.branch} />
      <div className="my-5 h-px bg-[#E0E5F2]" />
      <h3 className="mb-3 text-sm font-semibold text-[#1B163B]">
        Contact Details
      </h3>
      <SummaryLine label="Mobile Number" value={details?.mobile} />
      <SummaryLine label="Email" value={details?.email} />
      <div className="mt-6 flex gap-4 rounded-[6px] bg-[#6687FF1A] px-5 py-4 text-sm font-medium text-[#6687FF]">
        <FiInfo className="mt-1 shrink-0 text-lg" />
        <p>
          {details?.status ||
            "Beneficiary changes will be saved after verification."}
        </p>
      </div>
    </aside>
  );
}

export function PayoutBeneficiaryDetails() {
  const { beneficiaryId } = useParams();
  const routeKey = beneficiaryId ? decodeURIComponent(beneficiaryId) : "";
  const looksLikeId = /^ben_/i.test(routeKey);
  const fallbackName = looksLikeId ? "Beneficiary" : decodeBeneficiary(beneficiaryId);

  const detailsResource = usePayoutResource(
    {
      id: routeKey,
      initials: fallbackName
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 3),
      name: fallbackName,
      subtitle: "-",
      category: "Vendor",
      bank: "-",
      accountType: "Savings",
      accountNumber: "**** ----",
      ifsc: "-",
      status: "Pending",
      lastTransfer: "-",
      avatar: "bg-[#EDE9FE] text-[#6D28D9]",
      mobile: "-",
      email: "-",
      branch: "-",
      payoutAccount: "-",
      payoutAccountId: "-",
      payoutAccountMeta: "-",
      remarks: "-",
      tags: [] as string[],
      nameAsPerBank: "-",
      createdAt: "-",
      updatedAt: "-",
      verifiedAt: "-",
      createdBy: "-",
      verificationStatus: "Pending",
    },
    async (signal) => {
      if (!looksLikeId) {
        // Legacy name-slug links: resolve from list.
        const rows = await payoutApi.beneficiaries({}, signal);
        const match = rows.find((row) => {
          const mapped = beneficiaryFromApiRecord(row, { name: fallbackName });
          return mapped.name.toLowerCase() === fallbackName.toLowerCase();
        });
        if (!match) throw new Error("Beneficiary not found");
        return beneficiaryDetailFromApiRecord(match, { name: fallbackName });
      }
      const row = await payoutApi.beneficiaryDetails(routeKey, signal);
      return beneficiaryDetailFromApiRecord(row, { name: fallbackName });
    },
    [routeKey],
  );

  const details = detailsResource.data;
  const name = details.name || fallbackName;
  const status = details.status || "Pending";
  const editKey = details.id || routeKey;
  const bulkAttachment = useMemo(
    () => findBulkBeneficiaryAttachment(editKey),
    [editKey],
  );

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <Link
            to="/payouts/beneficiaries"
            className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
          >
            <FiArrowLeft />
            Back to Beneficiaries
          </Link>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Beneficiary Details
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Review beneficiary profile, bank account and payout access.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/payouts/beneficiaries/edit/${encodeURIComponent(editKey)}`}
            className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-6 text-sm font-semibold text-white"
          >
            Edit
          </Link>
          <Link
            to={`/payouts/beneficiaries/delete/${encodeURIComponent(editKey)}/verify`}
            className="flex h-10 items-center rounded-[6px] border border-[#EF4444] bg-white px-6 text-sm font-semibold text-[#EF4444]"
          >
            Delete
          </Link>
        </div>
      </div>

      {detailsResource.error && isPayoutApiMode() && (
        <div className="mb-4 rounded-[6px] bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#991B1B]">
          {detailsResource.error}
        </div>
      )}

      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-7 new-shadow">
        <div className="mb-6 flex items-center gap-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EDE9FE] text-lg font-semibold text-[#6D28D9]">
            {details.initials ||
              name
                .split(" ")
                .map((part) => part[0])
                .join("")
                .slice(0, 3)}
          </span>
          <div>
            <h2 className="text-[20px] font-semibold text-[#1B163B]">
              {name}
            </h2>
            <p className="mt-1 text-sm font-medium text-[#505E8E]">
              {details.subtitle || "-"}
            </p>
            <span
              className={
                "mt-2 inline-flex items-center gap-1 rounded-[5px] px-2 py-1 text-xs font-semibold " +
                (statusBadgeClass[status] || statusBadgeClass.Pending)
              }
            >
              {status === "Verified" && <FiCheckCircle className="text-xs" />}
              {status === "Pending" && <FiClock className="text-xs" />}
              {status}
            </span>
          </div>
        </div>

        <h3 className="mt-2 text-[16px] font-semibold text-[#1B163B]">
          Beneficiary Details
        </h3>
        <DetailLine label="Beneficiary Name" value={displayDetailValue(details.name)} />
        <DetailLine label="Name as per Bank" value={displayDetailValue(details.nameAsPerBank)} />
        <DetailLine label="Beneficiary Category" value={displayDetailValue(details.category)} />
        <DetailLine label="Payout Bank Account" value={displayDetailValue(details.payoutAccount)} />
        <DetailLine label="Payout Account ID" value={displayDetailValue(details.payoutAccountId)} />
        <DetailLine label="Payout Account Meta" value={displayDetailValue(details.payoutAccountMeta)} />
        <DetailLine
          label="Mobile Number"
          value={details.mobile && details.mobile !== "-" ? `+91 ${details.mobile}` : "-"}
        />
        <DetailLine label="Email" value={displayDetailValue(details.email)} />

        <h3 className="mt-8 text-[16px] font-semibold text-[#1B163B]">
          Bank Details
        </h3>
        <DetailLine label="Bank Account Number" value={displayDetailValue(details.accountNumber)} />
        <DetailLine label="IFSC Code" value={displayDetailValue(details.ifsc)} />
        <DetailLine label="Bank Name" value={displayDetailValue(details.bank)} />
        <DetailLine label="Account Type" value={displayDetailValue(details.accountType)} />
        <DetailLine label="Branch Name" value={displayDetailValue(details.branch)} />

        <h3 className="mt-8 text-[16px] font-semibold text-[#1B163B]">
          Additional Information
        </h3>
        <DetailLine label="Purpose / Remarks" value={displayDetailValue(details.remarks)} />
        <DetailLine label="Tags" value={displayDetailValue(details.tags)} />

        <h3 className="mt-8 text-[16px] font-semibold text-[#1B163B]">
          Activity Details
        </h3>
        <DetailLine label="Last Transfer" value={displayDetailValue(details.lastTransfer)} />
        <DetailLine label="Verification Status" value={displayDetailValue(details.verificationStatus || status)} />
        <DetailLine label="Created On" value={displayDetailValue(details.createdAt)} />
        <DetailLine label="Created By" value={displayDetailValue(details.createdBy)} />
        <DetailLine label="Verified On" value={displayDetailValue(details.verifiedAt)} />
        <DetailLine label="Last Updated" value={displayDetailValue(details.updatedAt)} />
      </section>

      {bulkAttachment && (
        <section className="mt-5 max-w-[520px] rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
            Attachment
          </h3>
          <div className="flex w-full items-center justify-between rounded-[6px] border border-[#E0E5F2] px-4 py-3 text-left">
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] bg-[#FEE2E2] text-xs font-semibold text-[#EF4444]">
                {bulkAttachment.ext}
              </span>
              <span className="min-w-0 text-sm font-semibold text-[#1B163B]">
                <span className="block truncate">{bulkAttachment.fileName}</span>
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  {bulkAttachment.size} - {bulkAttachment.rowLabel}
                </span>
              </span>
            </span>
            <FiFileText className="shrink-0 text-[#505E8E]" />
          </div>
        </section>
      )}
    </div>
  );
}

export function PayoutEditBeneficiary() {
  const { beneficiaryId } = useParams();
  const navigate = useNavigate();
  const routeKey = beneficiaryRouteKey(beneficiaryId);
  const editPath = `/payouts/beneficiaries/edit/${encodeURIComponent(routeKey)}`;

  const loaded = usePayoutResource(
    null as Record<string, unknown> | null,
    async (signal) => {
      if (!routeKey || !/^ben_/i.test(routeKey)) {
        throw new Error("Invalid beneficiary id");
      }
      return payoutApi.beneficiaryDetails(routeKey, signal);
    },
    [routeKey],
  );

  const accountsResource = usePayoutResource(
    [] as PayoutAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((rows) =>
        rows.map((row) => accountFromApiRecord(row, emptyPayoutAccount)),
      ),
  );
  const linkedAccounts = useMemo(
    () =>
      isPayoutApiMode() && !accountsResource.usingFallback && accountsResource.data.length
        ? accountsResource.data
        : [],
    [accountsResource.data, accountsResource.usingFallback],
  );
  const payoutBankAccountOptions = useMemo(
    () =>
      linkedAccounts.map((account) => ({
        label: account.name,
        value: account.id,
        meta: `${account.bank} **** ${account.accountNumber}`,
      })),
    [linkedAccounts],
  );

  const [payoutAccount, setPayoutAccount] = useState<SelectOption | null>(null);
  const [fundingAccountId, setFundingAccountId] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<SelectOption | null>(null);
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountType, setAccountType] = useState<SelectOption | null>(null);
  const [branchName, setBranchName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [tags, setTags] = useState("");
  const [bankName, setBankName] = useState("");
  const [suggestedBeneficiaryName, setSuggestedBeneficiaryName] = useState("");
  const [beneficiaryNameVerified, setBeneficiaryNameVerified] = useState(true);
  const [nameCheckLoading, setNameCheckLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hydrated, setHydrated] = useState(false);
  const [existingLast4, setExistingLast4] = useState("");

  React.useEffect(() => {
    if (!loaded.data || hydrated) return;
    const row = loaded.data;
    const mapped = beneficiaryFromApiRecord(row, { name: "Beneficiary" });
    const last4 = String(row.account_number_last4 || mapped.accountNumber || "").replace(/\D/g, "");
    setName(mapped.name);
    setCategory(optionFromLabel(beneficiaryCategoryOptions, mapped.category || "Vendor"));
    setMobile(String(row.mobile || ""));
    setEmail(String(row.email || ""));
    setAccountNumber("");
    setConfirmAccountNumber("");
    setIfsc(mapped.ifsc || "");
    setAccountType(optionFromLabel(accountTypeOptions, mapped.accountType || "Savings"));
    setBranchName(String(row.branch_name || row.branchName || ""));
    setFundingAccountId(String(row.funding_account_id || ""));
    setBankName(mapped.bank || "");
    setExistingLast4(last4);
    setBeneficiaryNameVerified(true);
    setHydrated(true);
    writeSessionJson("payoutBeneficiaryEditLast4", last4);
  }, [loaded.data, hydrated]);

  React.useEffect(() => {
    if (payoutAccount || !payoutBankAccountOptions.length) return;
    const matched =
      payoutBankAccountOptions.find((option) => option.value === fundingAccountId) ||
      payoutBankAccountOptions[0];
    setPayoutAccount(matched);
  }, [fundingAccountId, payoutAccount, payoutBankAccountOptions]);

  React.useEffect(() => {
    if (!isPayoutApiMode()) return;
    ensurePayoutsAccessToken().catch((err) => {
      console.warn("Payouts auth exchange failed on edit beneficiary", err);
    });
  }, []);

  const clearError = (field: string) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const applyLocalUatNameCheck = (nextName: string, acct: string, nextIfsc: string) => {
    const normalizedIfsc = nextIfsc.trim().toUpperCase();
    const isBharatUat =
      acct === BHARAT_UAT_BENE.account && normalizedIfsc === BHARAT_UAT_BENE.ifsc;
    if (isBharatUat) {
      if (namesMatch(nextName, BHARAT_UAT_BENE.name)) {
        setName(BHARAT_UAT_BENE.name);
        setBeneficiaryNameVerified(true);
        setSuggestedBeneficiaryName("");
        clearError("beneficiaryName");
        return true;
      }
      setBeneficiaryNameVerified(false);
      setSuggestedBeneficiaryName(BHARAT_UAT_BENE.name);
      setErrors((current) => ({
        ...current,
        beneficiaryName: "Name does not match the bank records.",
      }));
      return true;
    }

    const isKnownPnbUat =
      acct === LOCAL_UAT_BENE.account && normalizedIfsc.startsWith(LOCAL_UAT_BENE.ifscPrefix);
    if (!isKnownPnbUat) {
      setBeneficiaryNameVerified(false);
      setSuggestedBeneficiaryName("");
      setErrors((current) => ({
        ...current,
        beneficiaryName:
          "Bank name enquiry is unavailable. Use Bharat UAT account 123456789012 / HDFC0001782 or connect payouts API.",
      }));
      return false;
    }

    if (namesMatch(nextName, LOCAL_UAT_BENE.name)) {
      setName(LOCAL_UAT_BENE.name);
      setBeneficiaryNameVerified(true);
      setSuggestedBeneficiaryName("");
      clearError("beneficiaryName");
      return true;
    }

    setBeneficiaryNameVerified(false);
    setSuggestedBeneficiaryName(LOCAL_UAT_BENE.name);
    setErrors((current) => ({
      ...current,
      beneficiaryName: "Name does not match bank records. Use the suggested name.",
    }));
    return true;
  };

  const handleCheckBeneficiaryName = async () => {
    const nextName = name.trim();
    const acct = normalizeBankAccountNumber(accountNumber);
    const nextIfsc = normalizeIfsc(ifsc);

    if (!nextName) {
      setErrors((current) => ({
        ...current,
        beneficiaryName: "Enter beneficiary name before verification.",
      }));
      return;
    }
    if (!acct || !nextIfsc) {
      setErrors((current) => ({
        ...current,
        beneficiaryName: "Enter account number and IFSC before name check.",
      }));
      return;
    }

    setNameCheckLoading(true);
    try {
      const result = await payoutApi.beneficiaryNameCheck(
        { account_number: acct, ifsc: nextIfsc, name: nextName },
        stableIdempotencyKey("bene-name-check", `${acct}-${nextIfsc}-${nextName}`),
      );
      const bankNameFromApi =
        typeof result.name_as_per_bank === "string"
          ? result.name_as_per_bank.trim()
          : "";
      const matched = Boolean(result.matched);

      if (matched && bankNameFromApi) {
        setName(bankNameFromApi);
        setBeneficiaryNameVerified(true);
        setSuggestedBeneficiaryName("");
        clearError("beneficiaryName");
        return;
      }

      if (bankNameFromApi) {
        setBeneficiaryNameVerified(false);
        setSuggestedBeneficiaryName(bankNameFromApi);
        setErrors((current) => ({
          ...current,
          beneficiaryName: "Name does not match bank records. Use the suggested name.",
        }));
        return;
      }

      applyLocalUatNameCheck(nextName, acct, nextIfsc);
    } catch (error) {
      if (!applyLocalUatNameCheck(nextName, acct, nextIfsc)) {
        setBeneficiaryNameVerified(false);
        setErrors((current) => ({
          ...current,
          beneficiaryName: errorMessage(error, "Unable to verify beneficiary name."),
        }));
      }
    } finally {
      setNameCheckLoading(false);
    }
  };

  const handleCopyBeneficiaryName = async () => {
    if (!suggestedBeneficiaryName) return;
    try {
      await navigator.clipboard?.writeText(suggestedBeneficiaryName);
    } catch {
      // Clipboard can be unavailable in non-secure local contexts.
    }
    setName(suggestedBeneficiaryName);
    setSuggestedBeneficiaryName("");
    setBeneficiaryNameVerified(true);
    clearError("beneficiaryName");
  };

  const handleContinue = () => {
    const nextErrors: Record<string, string> = {};
    const trimmedMobile = normalizeIndianMobile(mobile);
    const trimmedEmail = normalizeEmail(email);
    const trimmedAccount = normalizeBankAccountNumber(accountNumber);
    const trimmedConfirmAccount = normalizeBankAccountNumber(confirmAccountNumber);
    const normalizedIfsc = normalizeIfsc(ifsc);

    if (!payoutAccount) {
      nextErrors.payoutAccount = "Select the payout bank account.";
    }
    if (!name.trim()) {
      nextErrors.beneficiaryName = "Enter beneficiary name.";
    } else if (name.trim().length < 3) {
      nextErrors.beneficiaryName = "Beneficiary name must be at least 3 characters.";
    } else if (!beneficiaryNameVerified) {
      nextErrors.beneficiaryName = "Verify beneficiary name as per bank records.";
    }
    if (!category) {
      nextErrors.category = "Select beneficiary category.";
    }
    nextErrors.mobile = validateRequiredIndianMobile(mobile);
    nextErrors.email = validateRequiredEmail(email);
    nextErrors.accountNumber = validateRequiredBankAccountNumber(accountNumber);
    nextErrors.confirmAccountNumber = validateRequiredConfirmAccountNumber(
      confirmAccountNumber,
      accountNumber,
    );
    nextErrors.ifsc = validateRequiredIfsc(ifsc);
    if (!accountType) {
      nextErrors.accountType = "Select account type.";
    }
    if (!branchName.trim()) {
      nextErrors.branchName = "Enter branch name.";
    }

    const compactErrors = Object.fromEntries(
      Object.entries(nextErrors).filter(([, value]) => value),
    ) as Record<string, string>;
    setErrors(compactErrors);
    if (Object.keys(compactErrors).length) {
      showPayoutNotification({
        tone: "error",
        message: contextualValidationMessage(
          "Edit beneficiary",
          compactErrors,
          "Edit beneficiary: complete the highlighted beneficiary and bank details.",
        ),
      });
      return;
    }

    const draft: BeneficiaryEditDraft = {
      beneficiaryId: routeKey,
      payoutAccount,
      name: name.trim(),
      beneficiaryName: name.trim(),
      category,
      mobile: trimmedMobile,
      mobileNumber: trimmedMobile,
      email: trimmedEmail,
      accountNumber: trimmedAccount,
      confirmAccountNumber: trimmedConfirmAccount,
      ifsc: normalizedIfsc,
      ifscCode: normalizedIfsc,
      accountType,
      branchName: branchName.trim(),
      remarks: remarks.trim(),
      tags: tags.trim(),
      beneficiaryNameVerified,
      bankName,
      existingLast4,
      fundingAccountId: payoutAccount?.value || fundingAccountId || undefined,
    };
    writeSessionJson("payoutBeneficiaryEditDraft", draft);
    navigate(`${editPath}/review`);
  };

  const summaryDetails = {
    category: optionText(category),
    payoutAccount: payoutAccount?.label,
    payoutAccountMeta: payoutAccount?.meta,
    bank: bankName || "-",
    accountNumber: maskedAccount(accountNumber, existingLast4),
    ifsc: displayText(ifsc),
    accountType: optionText(accountType),
    branch: displayText(branchName),
    mobile: mobile ? `+91 ${normalizeIndianMobile(mobile)}` : "-",
    email: displayText(email),
    status: "Beneficiary changes will be saved after verification.",
    tone: "edit" as const,
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Edit Beneficiary
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Update beneficiary details. Changes are saved on the verify step.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/payouts/beneficiaries"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#6687FF]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={handleContinue}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="flex h-10 items-center gap-4 rounded-[6px] px-7 text-sm font-semibold text-white"
          >
            Save & Continue
            <FiArrowRight className="text-lg" />
          </button>
        </div>
      </div>

      <PayoutBeneficiaryFlowSteps activeStep={1} />

      {loaded.error && (
        <div className="mb-4 rounded-[6px] bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#991B1B]">
          {loaded.error}
        </div>
      )}
      {isPayoutApiMode() && accountsResource.error && (
        <div className="mb-5 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-5 py-4 text-sm font-semibold text-[#EF4444]">
          Cannot load payout accounts: {accountsResource.error}.
        </div>
      )}
      {Object.keys(errors).length > 0 && (
        <div className="mb-5 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-5 py-4 text-sm font-semibold text-[#EF4444]">
          {contextualValidationMessage(
            "Edit beneficiary",
            errors,
            "Edit beneficiary: complete the highlighted beneficiary and bank details.",
          )}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_430px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-7 new-shadow">
          <h2 className="mb-7 text-[18px] font-semibold text-[#1B163B]">
            Beneficiary Details
          </h2>
          <div className="grid gap-x-8 gap-y-7 lg:grid-cols-2">
            <SearchableDropdown
              label="Payout Bank Account"
              placeholder="Select payout bank account"
              options={payoutBankAccountOptions}
              searchPlaceholder="Search payout accounts"
              value={payoutAccount}
              onChange={(value) => {
                setPayoutAccount(value);
                clearError("payoutAccount");
              }}
              error={errors.payoutAccount}
              required
            />
            <SearchableDropdown
              label="Category"
              placeholder="Select Category"
              options={beneficiaryCategoryOptions}
              searchPlaceholder="Search categories"
              value={category}
              onChange={(value) => {
                setCategory(value);
                clearError("category");
              }}
              error={errors.category}
              required
            />
            <div>
              <FieldLabel required>Mobile Number</FieldLabel>
              <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-4">
                <div className="flex h-11 items-center justify-center rounded-[6px] border border-[#E0E5F2] bg-white text-sm font-semibold text-[#1B163B]">
                  +91
                </div>
                <input
                  value={mobile}
                  onChange={(event) => {
                    setMobile(normalizeIndianMobile(event.target.value));
                    clearError("mobile");
                  }}
                  inputMode="numeric"
                  maxLength={10}
                  className={
                    "h-11 rounded-[6px] border bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0] " +
                    (errors.mobile ? "border-[#EF4444]" : "border-[#E0E5F2]")
                  }
                  placeholder="Enter mobile number"
                />
              </div>
              {errors.mobile && (
                <p className="mt-2 text-xs font-semibold text-[#EF4444]">
                  {errors.mobile}
                </p>
              )}
            </div>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              value={email}
              onChange={(value) => {
                setEmail(value);
                clearError("email");
              }}
              error={errors.email}
              type="email"
              required
            />
          </div>

          <h2 className="mt-8 mb-5 text-[18px] font-semibold text-[#1B163B]">
            Bank Details
          </h2>
          <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
            <TextInput
              label="Bank Account Number"
              placeholder="Enter account number"
              value={accountNumber}
              onChange={(value) => {
                setAccountNumber(normalizeBankAccountNumber(value));
                setBeneficiaryNameVerified(false);
                clearError("accountNumber");
                clearError("confirmAccountNumber");
              }}
              error={errors.accountNumber}
              inputMode="numeric"
              maxLength={18}
              required
            />
            <TextInput
              label="Confirm Account Number"
              placeholder="Re-enter account number"
              value={confirmAccountNumber}
              onChange={(value) => {
                setConfirmAccountNumber(normalizeBankAccountNumber(value));
                clearError("confirmAccountNumber");
              }}
              error={errors.confirmAccountNumber}
              inputMode="numeric"
              maxLength={18}
              required
            />
            <TextInput
              label="IFSC Code"
              placeholder="Enter IFSC code"
              value={ifsc}
              onChange={(value) => {
                setIfsc(value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11));
                setBeneficiaryNameVerified(false);
                clearError("ifsc");
              }}
              error={errors.ifsc}
              maxLength={11}
              required
            />
            <label className="block">
              <FieldLabel required>Beneficiary Name</FieldLabel>
              {suggestedBeneficiaryName && (
                <div className="mb-2 flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-[#F59E0B33] bg-[#F59E0B1A] px-3 py-2 text-sm font-medium text-[#1B163B]">
                  <span>
                    Name as per bank records:{" "}
                    <span className="font-semibold">
                      {suggestedBeneficiaryName}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyBeneficiaryName}
                    className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
                  >
                    <FiCopy />
                    Copy
                  </button>
                </div>
              )}
              <div
                className={
                  "flex h-11 items-center rounded-[6px] border bg-white px-3 " +
                  (errors.beneficiaryName ? "border-[#EF4444]" : "border-[#E0E5F2]")
                }
              >
                <input
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setBeneficiaryNameVerified(false);
                    setSuggestedBeneficiaryName("");
                    clearError("beneficiaryName");
                  }}
                  className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                  placeholder="Enter full name as per bank records"
                />
                {beneficiaryNameVerified ? (
                  <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-[#16A34A]">
                    <FiCheckCircle className="text-base" />
                    Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleCheckBeneficiaryName()}
                    disabled={nameCheckLoading}
                    className="shrink-0 text-sm font-semibold text-[#6687FF] disabled:opacity-50"
                  >
                    {nameCheckLoading ? "Checking..." : "Check Now"}
                  </button>
                )}
              </div>
              {errors.beneficiaryName && (
                <p className="mt-2 text-xs font-semibold text-[#EF4444]">
                  {errors.beneficiaryName}
                </p>
              )}
              <p className="mt-2 text-xs font-medium text-[#505E8E]">
                Check the name as per bank records before updating this payee.
              </p>
            </label>
            <SearchableDropdown
              label="Account Type"
              placeholder="Select Account Type"
              options={accountTypeOptions}
              searchPlaceholder="Search account types"
              value={accountType}
              onChange={(value) => {
                setAccountType(value);
                clearError("accountType");
              }}
              error={errors.accountType}
              required
            />
            <TextInput
              label="Branch Name"
              placeholder="Enter branch name"
              value={branchName}
              onChange={(value) => {
                setBranchName(value);
                clearError("branchName");
              }}
              error={errors.branchName}
              required
            />
          </div>

          <h2 className="mt-8 mb-5 text-[18px] font-semibold text-[#1B163B]">
            Additional Information{" "}
            <span className="text-sm font-medium text-[#505E8E]">
              (Optional)
            </span>
          </h2>
          <div className="grid gap-x-8 gap-y-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
            <label className="block">
              <FieldLabel>Purpose / Remarks</FieldLabel>
              <textarea
                value={remarks}
                onChange={(event) => setRemarks(event.target.value.slice(0, 150))}
                className="h-[72px] w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                placeholder="Add remarks or purpose for editing this beneficiary"
                maxLength={150}
              />
              <p className="mt-2 text-right text-xs font-medium text-[#505E8E]">
                {remarks.length} / 150
              </p>
            </label>
            <label className="block">
              <FieldLabel>Tags (Optional)</FieldLabel>
              <input
                value={tags}
                onChange={(event) => setTags(event.target.value)}
                className="h-11 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
                placeholder="Enter tags"
              />
              <p className="mt-2 text-xs font-medium text-[#505E8E]">
                Separate multiple tags with comma (,)
              </p>
            </label>
          </div>

          <div className="mt-7 flex items-center gap-3 text-sm font-medium text-[#505E8E]">
            <FiShield className="text-lg text-[#6687FF]" />
            All beneficiary details are encrypted and stored securely.
          </div>
        </section>
        <BeneficiarySummary
          name={name || decodeBeneficiary(beneficiaryId)}
          details={summaryDetails}
        />
      </div>
    </div>
  );
}

export function PayoutEditBeneficiaryReview() {
  const { beneficiaryId } = useParams();
  const routeKey = beneficiaryRouteKey(beneficiaryId);
  const editPath = `/payouts/beneficiaries/edit/${encodeURIComponent(routeKey)}`;
  const draft = readSessionJson<BeneficiaryEditDraft | null>(
    "payoutBeneficiaryEditDraft",
    null,
  );
  const last4 = readSessionJson("payoutBeneficiaryEditLast4", "");
  const beneficiaryName = displayText(draft?.beneficiaryName || draft?.name);
  const category = optionText(draft?.category);
  const payoutAccount = displayText(
    pickString(draft?.payoutAccount, ["label", "name"]),
  );
  const payoutAccountMeta = displayText(pickString(draft?.payoutAccount, ["meta"]));
  const accountType = optionText(draft?.accountType);
  const accountDisplay = maskedAccount(draft?.accountNumber, last4 || draft?.existingLast4);
  const ifsc = displayText(draft?.ifscCode || draft?.ifsc);
  const mobile = displayText(
    draft?.mobileNumber || draft?.mobile ? `+91 ${draft.mobileNumber || draft.mobile}` : "",
  );
  const email = displayText(draft?.email);
  const branchName = displayText(draft?.branchName);
  const remarks = displayText(draft?.remarks);
  const verificationStatus = draft?.beneficiaryNameVerified
    ? "Name verified"
    : "Pending verification";
  const summaryDetails = {
    category,
    payoutAccount,
    payoutAccountMeta,
    bank: displayText(draft?.bankName),
    accountNumber: accountDisplay,
    ifsc,
    accountType,
    branch: branchName,
    mobile,
    email,
    status: "Beneficiary changes will be saved after verification.",
    tone: "edit" as const,
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Review Beneficiary
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Review beneficiary changes before verification.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to={editPath}
            className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
          >
            <FiEdit2 />
            Edit
          </Link>
          <Link
            to={`${editPath}/verify`}
            className="flex h-10 items-center gap-4 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
          >
            Continue
            <FiArrowRight className="text-lg" />
          </Link>
        </div>
      </div>
      <PayoutBeneficiaryFlowSteps activeStep={2} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_390px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-5 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EDE9FE] text-lg font-semibold text-[#6D28D9]">
              {initialsFromName(beneficiaryName)}
            </div>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                {beneficiaryName}
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {category !== "-" && payoutAccount !== "-"
                  ? `${category} beneficiary for ${payoutAccount}`
                  : "-"}
              </p>
            </div>
          </div>

          <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2]">
            <DetailLine
              label="Payout Bank Account"
              value={
                <span>
                  {payoutAccount}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {payoutAccountMeta}
                  </span>
                </span>
              }
            />
            <DetailLine label="Beneficiary Name" value={beneficiaryName} />
            <DetailLine
              label="Category"
              value={
                <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
                  {category}
                </span>
              }
            />
            <DetailLine
              label="Contact"
              value={
                <span>
                  {mobile}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {email}
                  </span>
                </span>
              }
            />
            <DetailLine
              label="Bank Details"
              value={
                <span>
                  {accountType}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    A/C: {accountDisplay} | IFSC: {ifsc}
                  </span>
                </span>
              }
            />
            <DetailLine label="Branch" value={branchName} />
            <DetailLine label="Purpose / Remarks" value={remarks} />
            <DetailLine
              label="Verification Status"
              value={
                <span
                  className={
                    "inline-flex items-center gap-2 rounded-[5px] px-2 py-1 text-xs font-semibold " +
                    (draft?.beneficiaryNameVerified
                      ? "bg-[#DCFCE7] text-[#16A34A]"
                      : "bg-[#FEF3C7] text-[#F59E0B]")
                  }
                >
                  {verificationStatus}
                </span>
              }
            />
          </div>
          <div className="mt-6 flex gap-3 rounded-[6px] bg-[#1B163B0D] px-5 py-4 text-sm font-medium text-[#1B163B]">
            <FiInfo className="mt-0.5 shrink-0 text-lg" />
            Beneficiary changes will be submitted after the verification step.
          </div>
          <div className="mt-7 flex items-center justify-between">
            <Link
              to={editPath}
              className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <Link
              to={`${editPath}/verify`}
              className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
            >
              Continue to Verification
              <FiArrowRight />
            </Link>
          </div>
        </section>
        <BeneficiarySummary
          name={beneficiaryName || decodeBeneficiary(beneficiaryId)}
          details={summaryDetails}
        />
      </div>
    </div>
  );
}

export function PayoutEditBeneficiaryVerify() {
  const navigate = useNavigate();
  const { beneficiaryId } = useParams();
  const routeKey = beneficiaryRouteKey(beneficiaryId);
  const confirmPath = `/payouts/beneficiaries/edit/${encodeURIComponent(routeKey)}/confirm`;
  const draft = readSessionJson<BeneficiaryEditDraft | null>(
    "payoutBeneficiaryEditDraft",
    null,
  );
  const last4 = readSessionJson("payoutBeneficiaryEditLast4", "");
  const beneficiaryName = displayText(draft?.beneficiaryName || draft?.name);
  const summaryDetails = {
    category: optionText(draft?.category),
    payoutAccount: displayText(pickString(draft?.payoutAccount, ["label", "name"])),
    payoutAccountMeta: displayText(pickString(draft?.payoutAccount, ["meta"])),
    bank: displayText(draft?.bankName),
    accountNumber: maskedAccount(draft?.accountNumber, last4 || draft?.existingLast4),
    ifsc: displayText(draft?.ifscCode || draft?.ifsc),
    accountType: optionText(draft?.accountType),
    branch: displayText(draft?.branchName),
    mobile: draft?.mobileNumber || draft?.mobile ? `+91 ${draft.mobileNumber || draft.mobile}` : "-",
    email: displayText(draft?.email),
    status: "Details are encrypted and protected by Google Authenticator verification.",
    tone: "edit" as const,
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Verify Beneficiary Edit
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Confirm to save changes to payouts-service.
        </p>
      </div>
      <PayoutBeneficiaryFlowSteps activeStep={3} />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <OtpPanel
          title="Verify Beneficiary Update"
          description="Sensitive beneficiary edits require verification."
          cta="Continue"
          to={confirmPath}
          onSubmit={async ({ method, code }) => {
            if (!draft?.beneficiaryId) {
              navigate(confirmPath);
              return;
            }
            if (isPayoutMockMode()) {
              navigate(confirmPath);
              return;
            }
            const stepUpToken = isPayoutStepUpEnabled()
              ? await verifyStepUp({
                  purpose: "UPDATE_BENEFICIARY",
                  method,
                  code,
                })
              : undefined;
            const body = compactBody({
              name: draft.beneficiaryName || draft.name,
              category: optionText(draft.category, ""),
              mobile: draft.mobileNumber || draft.mobile,
              email: draft.email,
              ifsc: draft.ifscCode || draft.ifsc || undefined,
              account_number: draft.accountNumber || undefined,
              account_type: optionText(draft.accountType, ""),
              branch_name: draft.branchName,
              funding_account_id: draft.fundingAccountId || undefined,
            });
            const result = await payoutApi.updateBeneficiary(
              draft.beneficiaryId,
              body,
              stableIdempotencyKey("update-beneficiary", draft.beneficiaryId),
              stepUpToken,
            );
            writeSessionJson("payoutBeneficiaryEditResult", result);
            navigate(confirmPath);
          }}
        />
        <BeneficiarySummary
          name={beneficiaryName || decodeBeneficiary(beneficiaryId)}
          details={summaryDetails}
        />
      </div>
    </div>
  );
}

export function PayoutEditBeneficiaryConfirm() {
  const { beneficiaryId } = useParams();
  const routeKey = beneficiaryRouteKey(beneficiaryId);
  const draft = readSessionJson<BeneficiaryEditDraft | null>(
    "payoutBeneficiaryEditDraft",
    null,
  );
  const result = readSessionJson<Record<string, unknown>>(
    "payoutBeneficiaryEditResult",
    {},
  );
  const name =
    (typeof result.name_entered === "string" && result.name_entered) ||
    draft?.beneficiaryName ||
    draft?.name ||
    decodeBeneficiary(beneficiaryId);
  const last4 = readSessionJson("payoutBeneficiaryEditLast4", "");
  const beneficiaryIdValue =
    pickString(result, ["beneficiary_id", "beneficiaryId", "id"], "") ||
    draft?.beneficiaryId ||
    "-";
  const accountLast4 =
    pickString(result, ["account_number_last4", "accountNumberLast4"], "") ||
    draft?.accountNumber?.slice(-4) ||
    last4;
  const ifsc =
    pickString(result, ["ifsc", "ifsc_code"], "") ||
    draft?.ifscCode ||
    draft?.ifsc ||
    "-";
  const category =
    pickString(result, ["category"], "") || optionText(draft?.category, "Vendor");
  const accountType =
    pickString(result, ["account_type", "accountType"], "") ||
    optionText(draft?.accountType, "Savings");
  const payoutAccount =
    pickString(draft?.payoutAccount, ["label", "name"], "Payout Account");

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Confirm Beneficiary
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          The beneficiary details have been updated successfully.
        </p>
      </div>
      <PayoutBeneficiaryFlowSteps activeStep={4} />
      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 text-center new-shadow">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#DCFCE7] text-4xl text-[#16A34A]">
          <FiCheck />
        </div>
        <h2 className="text-[22px] font-semibold text-[#1B163B]">
          Beneficiary Updated Successfully!
        </h2>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          {name} is now updated for payout transfers.
        </p>

        <div className="mt-7 overflow-hidden rounded-[8px] border border-[#E0E5F2] text-left">
          <DetailLine
            label="Beneficiary ID"
            value={
              <span className="flex items-center justify-between gap-4">
                {beneficiaryIdValue}
                <FiCopy className="shrink-0 text-[#505E8E]" />
              </span>
            }
          />
          <DetailLine label="Beneficiary Name" value={name} />
          <DetailLine
            label="Payout Bank Account"
            value={
              <span>
                {payoutAccount}
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  {pickString(draft?.payoutAccount, ["meta"], "-")}
                </span>
              </span>
            }
          />
          <DetailLine
            label="Bank Details"
            value={
              <span>
                {accountType}
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  A/C: **** {accountLast4 || "----"} | IFSC: {ifsc}
                </span>
              </span>
            }
          />
          <DetailLine
            label="Category"
            value={
              <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
                {category}
              </span>
            }
          />
          <DetailLine
            label="Status"
            value={
              <span className="inline-flex items-center gap-2 rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
                <FiUserCheck />
                Updated
              </span>
            }
          />
        </div>
      </section>

      <div className="mt-8 flex max-w-[760px] flex-wrap items-center justify-between gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <Link
          to="/payouts/beneficiaries"
          className="flex h-10 items-center rounded-[6px] border border-[#1B163B] px-7 text-sm font-semibold text-[#1B163B]"
        >
          View Beneficiaries
        </Link>
        <Link
          to={`/payouts/beneficiaries/${encodeURIComponent(draft?.beneficiaryId || routeKey)}`}
          className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
        >
          View Details
          <FiArrowRight />
        </Link>
      </div>
    </div>
  );
}

export function PayoutDeleteBeneficiaryVerify() {
  const navigate = useNavigate();
  const { beneficiaryId } = useParams();
  const routeKey = beneficiaryRouteKey(beneficiaryId);
  const name = decodeBeneficiary(beneficiaryId);
  const confirmPath = `/payouts/beneficiaries/delete/${encodeURIComponent(routeKey)}/confirm`;

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Delete Beneficiary
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Verify before permanently deleting this beneficiary.
        </p>
      </div>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,760px)_380px]">
        <OtpPanel
          title="Verify Beneficiary Deletion"
          description="Deleting a beneficiary requires Google Authenticator verification."
          cta="Continue"
          to={confirmPath}
          danger
          onSubmit={async ({ method, code }) => {
            if (!isPayoutMockMode() && routeKey) {
              const stepUpToken = isPayoutStepUpEnabled()
                ? await verifyStepUp({
                    purpose: "DELETE_BENEFICIARY",
                    method,
                    code,
                  })
                : undefined;
              await payoutApi.deleteBeneficiary(
                routeKey,
                stableIdempotencyKey("delete-beneficiary", routeKey),
                stepUpToken,
              );
            }
            navigate(confirmPath);
          }}
        />
        <BeneficiarySummary name={name} />
      </div>
    </div>
  );
}

export function PayoutDeleteBeneficiaryConfirm() {
  const { beneficiaryId } = useParams();
  const name = decodeBeneficiary(beneficiaryId);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-7 text-center new-shadow">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FEE2E2] text-3xl text-[#EF4444]">
          <FiTrash2 />
        </div>
        <h1 className="mt-5 text-[22px] font-semibold text-[#1B163B]">
          Beneficiary Deleted
        </h1>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          {name} has been removed from payout beneficiaries.
        </p>
        <Link
          to="/payouts/beneficiaries"
          className="mt-7 inline-flex h-10 items-center rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
        >
          Back to Beneficiaries
        </Link>
      </section>
    </div>
  );
}

